import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { requireSession } from '@/lib/ensureSession';
import { useUserStore } from '@/store/userStore';

// ════════════════════════════════════════════════════════════════
// «Путь со Славой» — данные курса (Фаза 2)
// Читает course_steps + user_course_progress, ведёт прогресс.
// ════════════════════════════════════════════════════════════════

export interface CourseStep {
  id: number;
  day: number;
  phase: 'start' | 'week1' | 'week2' | 'week3';
  is_free: boolean;
  title: string;
  teaser_title?: string | null;
  slava_line: string;
  description?: string | null;
  task: string;
  example?: string | null;
  how_chips: string[];
  action_type: 'photo_meal' | 'photo_face' | 'breathing' | 'meditation' | 'measure' | 'reflection' | 'none';
  action_ref?: string | null;
  fixation_type: 'hunger_fullness' | 'mood' | 'none';
  why_text?: string | null;
  source_ref?: string | null;
  strength?: string | null;
}

export interface CourseProgress {
  user_id: string;
  started_at?: string | null;
  current_day: number;
  completed_days: number[];
  point_a?: any;
  point_a_photo_path?: string | null;
  point_b_photo_path?: string | null;
  completed_at?: string | null;
  last_completed_date?: string | null;
  reward_unlocked: boolean;
  // Миграция 034: «Путь пройден» на главной показывается один раз,
  // дальше — только если человек включил галочку в Статистике.
  finished_seen_at?: string | null;
  show_on_home?: boolean;
}

export function useCourse() {
  const { profile } = useUserStore();
  const [steps, setSteps] = useState<CourseStep[]>([]);
  const [progress, setProgress] = useState<CourseProgress | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const { data: s } = await supabase
        .from('course_steps')
        .select('*')
        .eq('active', true)
        .order('day', { ascending: true });
      setSteps((s ?? []) as CourseStep[]);

      const { data: p } = await supabase
        .from('user_course_progress')
        .select('*')
        .eq('user_id', profile.id)
        .maybeSingle();
      setProgress((p as CourseProgress) ?? null);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => { load(); }, [load]);

  // Старт курса — создаёт строку прогресса с точкой А (+ опц. фото лица)
  async function startCourse(pointA?: any, photoPath?: string | null) {
    if (!profile) return null;
    if (!(await requireSession())) return null;
    const { data, error } = await supabase
      .from('user_course_progress')
      .upsert(
        {
          user_id: profile.id,
          started_at: new Date().toISOString(),
          current_day: 1,
          point_a: pointA ?? null,
          point_a_photo_path: photoPath ?? null,
        },
        { onConflict: 'user_id' },
      )
      .select()
      .single();
    if (error) { console.error('[course] start error:', error); return null; }
    setProgress(data as CourseProgress);
    return data as CourseProgress;
  }

  // Загрузка приватного фото лица: slot 'a' (день 1) или 'b' (день 21)
  async function uploadProgressPhoto(file: File, slot: 'a' | 'b'): Promise<string | null> {
    if (!profile) return null;
    if (!(await requireSession())) return null;
    const path = `${profile.id}/${slot}.jpg`;
    const { error } = await supabase.storage
      .from('progress-photos')
      .upload(path, file, { upsert: true, contentType: file.type || 'image/jpeg' });
    if (error) { console.error('[course] photo upload error:', error); return null; }
    return path;
  }

  // Приватный bucket → нужен подписанный URL для показа
  async function getSignedPhotoUrl(path?: string | null): Promise<string | null> {
    if (!path) return null;
    const { data } = await supabase.storage.from('progress-photos').createSignedUrl(path, 3600);
    return data?.signedUrl ?? null;
  }

  async function setPointBPhoto(path: string) {
    if (!profile) return;
    const { data } = await supabase
      .from('user_course_progress')
      .update({ point_b_photo_path: path })
      .eq('user_id', profile.id).select().single();
    if (data) setProgress(data as CourseProgress);
  }

  async function unlockReward() {
    if (!profile) return;
    const { data } = await supabase
      .from('user_course_progress')
      .update({ reward_unlocked: true })
      .eq('user_id', profile.id).select().single();
    if (data) setProgress(data as CourseProgress);
  }

  // Завершение шага дня + фиксация ощущения → продвигаем прогресс
  async function completeStep(day: number, fixation?: any) {
    if (!profile || !progress) return;
    if (!(await requireSession())) return;

    await supabase
      .from('user_course_step_log')
      .upsert({ user_id: profile.id, day, fixation: fixation ?? null }, { onConflict: 'user_id,day' });

    const completed = Array.from(new Set([...(progress.completed_days ?? []), day])).sort((a, b) => a - b);
    const nextDay = Math.min(21, Math.max(progress.current_day, day + 1));
    const patch: Record<string, any> = {
      completed_days: completed,
      current_day: nextDay,
      last_completed_date: todayLocal(),
    };
    if (day >= 21) {
      patch.completed_at = new Date().toISOString();
      // Достижение «Путь пройден» — из тех, что стоит показать друзьям (035)
      supabase.rpc('check_achievement', { uid: profile.id, ach_id: 'course_done', new_progress: 1 }).then(() => {});
    }

    const { data, error } = await supabase
      .from('user_course_progress')
      .update(patch)
      .eq('user_id', profile.id)
      .select()
      .single();
    if (error) { console.error('[course] complete error:', error); return; }
    setProgress(data as CourseProgress);
  }

  return {
    steps, progress, loading,
    startCourse, completeStep, reload: load,
    uploadProgressPhoto, getSignedPhotoUrl, setPointBPhoto, unlockReward,
  };
}

// Доступен ли шаг: дни 1-3 бесплатны; дальше — по активной подписке
export function isStepUnlocked(step: CourseStep, subscriptionStatus?: string | null): boolean {
  if (step.is_free) return true;
  return subscriptionStatus === 'active' || subscriptionStatus === 'trial';
}

// Локальная дата пользователя (YYYY-MM-DD)
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Можно ли делать шаг сегодня: 1 шаг в день — следующий откроется завтра
export function canStartToday(progress: CourseProgress | null): boolean {
  if (!progress) return true;
  if (!progress.last_completed_date) return true;
  return progress.last_completed_date < todayLocal();
}
